<template>
	<DialogPanel
		class="template-browser-panel"
		:scroll-body="false"
		@close="$emit( `close` )"
	>
		<template #title-text>{{ translate( "title" ) }}</template>

		<template #sub-header v-if="!showCategoryList">
			<div class="selector-header">
				<TeamingGroupedDropDown
					class="category-dropdown"
					:disabled="disabled"
					:options="templateCategories"
					:value="selectedTemplateCategory"
					@change="$emit( `categoryChanged`, $event.value )"
				/>
			</div>
		</template>

		<template #body>
			<div class="sub-body">
				<div v-if="showCategoryList" class="sub-body--left">
					<div v-for="category in templateCategories"
						:key="category.group"
						class="category-group"
					>
						<label class="group-name">{{ category.group }}</label>
						<TeamingChip2 v-for="option in category.options"
							:key="option.value"
							class="template-category"
							size="SMALL"
							:selected="option.value === selectedTemplateCategory"
							@click="$emit( `categoryChanged`, option.value )"
						>
							<span v-html="formatText( option )"></span>
						</TeamingChip2>
					</div>
				</div>
				<div class="sub-body--right">
					<div class="templates">
						<p v-if="description" class="description">{{ description }}</p>
						<CarouselTemplate
							v-for="template in templates"
							:key="template.meetingTemplateId"
							:template="template"
							v-on="bubble( `applyTemplate`, `previewTemplate`, `deleteTemplate` )"
						/>
					</div>
				</div>
			</div>
		</template>
	</DialogPanel>
</template>

<script>
/* eslint-disable vue/no-unused-components,vue/no-unused-properties */
import {
	Enum
} from "@teaming/common-enums";

import {
	EventsMixin,
	LabeledDropDown,
	DialogPanel,
	TeamingAlert,
	TeamingButton,
	TeamingChip2,
	TeamingGroupedDropDown
} from "@teaming/common-ui";

import CarouselTemplate from "./CarouselTemplate.vue";

export const DISPLAY_MODE = Enum.create( {
	COMPACT: "COMPACT",
	NORMAL: "NORMAL",
} );

export default {
	name: "TemplateBrowserPanel",
	emits: [ "close", "categoryChanged", "applyTemplate", "previewTemplate", "deleteTemplate", ],
	mixins: [ EventsMixin, ],
	components: {
		LabeledDropDown,
		TeamingButton,
		TeamingChip2,
		DialogPanel,
		TeamingGroupedDropDown,
		CarouselTemplate,
		TeamingAlert,
	},
	props: {
		disabled: {
			type: Boolean,
			default: false,
		},
		mode: {
			type: String,
			default: "NORMAL",
			validator( value ) {
				return DISPLAY_MODE.includes( value );
			},
		},
		/**
		 * Option = { text:String, value:String, suffix:String, description:String, placeholder:String }
		 * { group:String, options:[Option] }
		 **/
		templateCategories: {
			type: Array,
			required: true,
		},
		/** TemplateCategory | teamId */
		selectedTemplateCategory: {
			type: String,
			required: true,
		},
		templates: {
			type: Array,
			required: true,
		},
	},
	computed: {
		showCategoryList() {
			return this.mode === DISPLAY_MODE.NORMAL;
		},
		flattenedOptions() {
			return this.templateCategories.reduce( ( acc, group ) => {
				return [
					...acc,
					...group.options,
				];
			}, [] );
		},
		selectedOption() {
			const { selectedTemplateCategory, flattenedOptions, } = this;
			return flattenedOptions.find( option => option.value === selectedTemplateCategory );
		},
		description() {
			const { selectedOption, templates, } = this;
			if ( templates.length ) {
				return selectedOption.description;
			}
			return selectedOption.placeholder;
		},
	},
	methods: {
		translate( key, args = {} ) {
			const { name, } = this.$options;
			return this.$t( `common-team.components.${ name }.${ key }`, args );
		},
		formatText( option ) {
			const breakSpaceText = option.text.replace( "@", "&ZeroWidthSpace;@" );
			return `${ breakSpaceText } ${ option.suffix }`.trim();
		},
	},
};
</script>

<style lang="scss" scoped>
$subBodyBackgroundColor--left: $white;
$descriptionColor: $color1-700;
$templateCategoryColor: $color1-500;
$groupNameColor: $color2-500;
$selectorHeaderBackgroundColor: $white;

.template-browser-panel {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;

	position: absolute;
	top: 0;
	bottom: 0;
	width: 100%;
}

.template-browser-panel .sub-body {
	flex: 1;
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	gap: 1rem;

	padding: 0 0 0 1rem;

	overflow-y: hidden;
	height: 100%;

	text-align: left;

	@include for-size('md') {
		padding: 0;
	}
}

.template-browser-panel .sub-body--left {
	flex: 1;
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 0.5rem;

	@include auto-y-scroll();

	border-right: 0.0625rem solid $borderColor--dark;
	padding-top: 1rem;
	padding-right: 1rem;
	padding-left: 1.5rem;

	background-color: $subBodyBackgroundColor--left;
}

.template-browser-panel .sub-body--right {
	flex: 5;
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 2rem;

	text-align: left;

	padding-top: 1rem;
	padding-right: 1.5rem;
}

.template-browser-panel .selector-header {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 2rem;

	padding: 1rem;

	background-color: $selectorHeaderBackgroundColor;
}

.template-browser-panel .description {
	@include font-and-line-for(0);
	font-weight: 450;
	color: $descriptionColor;

	cursor: default;
}

.template-browser-panel .templates {
	flex: 1;
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 1rem;

	@include auto-y-scroll();

	@include font-and-line-for(0);
	font-weight: 450;

	padding: 0 0 0 0.5rem;
	margin-bottom: 1.5rem;

	// stack templates 2xN
	@include for-size('sm') {
		flex: unset;
		flex-direction: row;
		align-items: flex-start;
		flex-wrap: wrap;
		.carousel-template {
			width: 45%;
		}
	}
}

.template-browser-panel .template-category {
	border: none;

	&.teaming-chip2--default {
		color: $templateCategoryColor;
	}

	&:last-of-type {
		margin-bottom: 1rem;
	}

	&:only-child {
		margin-bottom: unset;
	}
}

.template-browser-panel .category-group {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 0.5rem;
}

.template-browser-panel .group-name {
	@include font-and-line-for(-1);
	font-weight: 600;
	text-transform: uppercase;
	color: $groupNameColor;
}
</style>

<style lang="scss">
$bodyBackgroundColor: $color1-050;

.template-browser-panel .body {
	background-color: $bodyBackgroundColor;
}

.template-browser-panel .category-dropdown .teaming-drop-down__options {
	height: 18rem;
	@include auto-y-scroll();
}
</style>
